import { getDynamicEnvVarsInClient } from '@/utils/envHelper';
import axios from 'axios';
import { toast } from 'sonner';

export const getUserDetails = async (
  id: string,
  tenantId: any,
  tenantName: any,
  accessToken: any
) => {
  const { NEXT_PUBLIC_API_URL } = await getDynamicEnvVarsInClient();

  try {
    const response = await axios.get(`${NEXT_PUBLIC_API_URL}/users/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-TENANT-ID': tenantId,
        'X-TENANT-NAME': tenantName,
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const userData = await response?.data;
    return userData;
  } catch (error) {
    console.log('Error in getting user info:', error);
    return {};
  }
};

export const editUserDetails = async (
  userId: string,
  updateValues: any,
  tenantId: any,
  tenantName: any,
  accessToken: any
) => {
  try {
    const { NEXT_PUBLIC_API_URL } = getDynamicEnvVarsInClient();

    const response = await axios.post(
      `${NEXT_PUBLIC_API_URL}/users/${userId}`,
      updateValues,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-TENANT-ID': tenantId,
          'X-TENANT-NAME': tenantName,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response;
  } catch (error) {
    console.log('Error updating user details:', error);
    return { status: 400, error };
  }
};

export const updateEmail = async (
  userId: string,
  email: any,
  tenantId: any,
  tenantName: any,
  accessToken: any
) => {
  try {
    const { NEXT_PUBLIC_API_URL } = getDynamicEnvVarsInClient();

    const response = await axios.put(
      `${NEXT_PUBLIC_API_URL}/update-email/${userId}`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-TENANT-ID': tenantId,
          'X-TENANT-NAME': tenantName,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response;
  } catch (error) {
    console.log('Error updating user email:', error);
    return { status: 400, error };
  }
};

export const updateEmailChecker = async (
  userId: string,
  email: any,
  tenantId: any,
  tenantName: any,
  accessToken: any
) => {
  try {
    const { NEXT_PUBLIC_API_URL } = getDynamicEnvVarsInClient();

    const response = await axios.post(
      `${NEXT_PUBLIC_API_URL}/update-email-checker/${userId}`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-TENANT-ID': tenantId,
          'X-TENANT-NAME': tenantName,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.data?.error === -1) {
      // If the response indicates that the email is valid for update
      return { success: true };
    } else {
      // If the response indicates that something is wrong (email blocked, in use, etc.)
      return { success: false, error: response.data.data };
    }
  } catch (error) {
    console.log('Error updating user email:', error);
    return { success: false, error: 'An error occurred while checking email' };
  }
};

export const uploadImageToS3 = async (
  userId: string,
  file: any,
  tenantId: any,
  tenantName: any,
  accessToken: any
) => {
  const { NEXT_PUBLIC_CW_AUTH_URL } = getDynamicEnvVarsInClient();

  try {
    const response = await axios.post(
      `${NEXT_PUBLIC_CW_AUTH_URL}/users/upload/image/${userId}`,
      file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-TENANT-ID': tenantId,
          'X-TENANT-NAME': tenantName,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response?.data;
  } catch (e: any) {
    if (e.response && e.response.status === 413) {
      toast.error(
        'Image size exceeds the maximum allowed limit. Please upload a smaller image.'
      );
    } else {
      toast.error(
        'Something went wrong while uploading the image. Please try again.'
      );
    }
  }
};

export async function getUserByToken(
  tenantId: string,
  tenantName: string,
  token: string,
  base_url: string
) {
  if (
    tenantId &&
    tenantId !== 'null' &&
    tenantName &&
    tenantName !== 'null' &&
    token &&
    token !== 'null'
  ) {
    try {
      const response = await axios.get(`${base_url}/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-TENANT-ID': tenantId,
          'X-TENANT-NAME': tenantName,
        },
      });

      // 🔄 FIXED: Check if backend returned an error response
      if (response?.data?.error === 1) {
        console.warn('Backend returned error response:', response.data.message);
        return null; // Return null to indicate error
      }

      return response?.data?.data;
    } catch (error) {
      console.error(`Error in getting user by token:`, error);
      throw error; // Re-throw error so calling code can handle it
    }
  }
}

export async function isMySessionValid(
  tenantId: string,
  tenantName: string,
  token: string,
  base_url: string
) {
  try {
    const response = await axios.get(`${base_url}/validate/session`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-TENANT-ID': tenantId,
        'X-TENANT-NAME': tenantName,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw new Error('Token expired.');
  }
}
export async function getAccessTokenFromRefreshToken(
  refreshToken: string,
  base_url: string,
  app_id?: string
) {
  try {
    const response = await axios.post(
      `${base_url}/token/generate`,
      {
        ...(app_id ? { app_id } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to generate access token');
  }
}

export async function fetchPermissions(
  tenantId: any,
  tenantName: any,
  accessToken: any
) {
  try {
    const { NEXT_PUBLIC_CW_AUTH_URL } = getDynamicEnvVarsInClient();

    const response = await axios.get(
      `${NEXT_PUBLIC_CW_AUTH_URL}/me/permission`,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-TENANT-ID': tenantId,
          'X-TENANT-NAME': tenantName,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error getting user's permission:`, error);
  }
}
